/**
 * The studio config in the SPA (D22, PLAN §7.1a): the JSON editor's
 * validation — the same schema the hosts validate with
 * (`@acp/engine/studioConfig.js`'s `studioConfigSchema`, cross-references
 * included), so an error shows while typing, at its path, rather than as a
 * 422 on save — and the structured summary the studio page shows.
 *
 * What is saved is the document as written (defaults NOT filled in), exactly
 * as `acp studios create --file` sends it.
 */
import { MAX_STUDIO_CONFIG_CHARS } from '@acp/api-contract/studios.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import { type StudioConfig, studioConfigSchema } from '@acp/engine/studioConfig.js';

export type ConfigProblem = {
  /** Dotted, `(document)` for the whole: `pieceTypes.camera.sourceLayer`, `adlibPanels.0.items.2.action`. */
  readonly path: string;
  readonly message: string;
};

export type ConfigCheck =
  | { readonly ok: true; readonly document: { readonly [key: string]: JsonValue }; readonly config: StudioConfig }
  | { readonly ok: false; readonly problems: readonly ConfigProblem[] };

export const DOCUMENT_PATH = '(document)';

function pathOf(path: readonly PropertyKey[]): string {
  return path.length === 0 ? DOCUMENT_PATH : path.map(String).join('.');
}

function isObject(value: unknown): value is { readonly [key: string]: JsonValue } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Check the editor's text: JSON, an object, within the size limit, and a
 * valid studio config. Problems are sorted by path so the list does not jump
 * around while typing.
 */
export function checkConfigText(text: string): ConfigCheck {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch (err) {
    return { ok: false, problems: [{ path: DOCUMENT_PATH, message: `not JSON: ${err instanceof Error ? err.message : String(err)}` }] };
  }
  if (!isObject(json)) return { ok: false, problems: [{ path: DOCUMENT_PATH, message: 'a studio config is a JSON object' }] };
  const size = JSON.stringify(json).length;
  if (size > MAX_STUDIO_CONFIG_CHARS) {
    return { ok: false, problems: [{ path: DOCUMENT_PATH, message: `serialises to ${String(size)} characters; at most ${String(MAX_STUDIO_CONFIG_CHARS)}` }] };
  }
  const parsed = studioConfigSchema.safeParse(json);
  if (parsed.success) return { ok: true, document: json, config: parsed.data };
  const problems = parsed.error.issues.map((issue) => ({ path: pathOf(issue.path), message: issue.message }));
  return { ok: false, problems: problems.sort((a, b) => a.path.localeCompare(b.path)) };
}

/**
 * The server's `validation-failed` issues (paths prefixed `config.`, and
 * `/`-separated by some hosts) as editor problems.
 */
export function serverProblems(issues: readonly { readonly path: string; readonly message: string }[]): ConfigProblem[] {
  return issues.map((issue) => {
    const dotted = issue.path.replaceAll('/', '.').replace(/^\./, '');
    const path = dotted.startsWith('config.') ? dotted.slice('config.'.length) : dotted === 'config' ? DOCUMENT_PATH : dotted;
    return { path: path === '' ? DOCUMENT_PATH : path, message: issue.message };
  });
}

// ─── the structured summary ───

export type StudioSummaryView = {
  readonly devices: readonly { readonly id: string; readonly type: string; readonly standbyPolicy: string; readonly connection: string }[];
  readonly mappings: readonly { readonly layer: string; readonly device: string; readonly lookahead: string; readonly oneShot: boolean }[];
  readonly sourceLayers: readonly { readonly id: string; readonly label: string }[];
  readonly outputLayers: readonly { readonly id: string; readonly label: string; readonly isPgm: boolean }[];
  readonly pieceTypes: readonly {
    readonly id: string;
    readonly label: string;
    readonly sourceLayer: string;
    readonly lifespan: string;
    readonly params: readonly { readonly name: string; readonly type: string; readonly required: boolean }[];
  }[];
  readonly actions: readonly { readonly id: string; readonly label: string; readonly steps: readonly string[] }[];
  readonly adlibPanels: readonly { readonly id: string; readonly label: string; readonly items: readonly string[] }[];
  readonly minimumTakeSpanMs: number;
};

/** `host:port` when the options say so, else the options as JSON. */
function connectionOf(options: Readonly<Record<string, JsonValue>>): string {
  const host = options['host'];
  const port = options['port'];
  if (typeof host === 'string' && (typeof port === 'number' || typeof port === 'string')) return `${host}:${String(port)}`;
  return Object.keys(options).length === 0 ? '–' : JSON.stringify(options);
}

export function studioSummary(config: StudioConfig): StudioSummaryView {
  return {
    devices: Object.entries(config.devices).map(([id, d]) => ({ id, type: d.type, standbyPolicy: d.standbyPolicy, connection: connectionOf(d.options) })),
    mappings: Object.entries(config.mappings).map(([layer, m]) => ({ layer, device: m.device, lookahead: m.lookahead, oneShot: m.oneShot })),
    sourceLayers: Object.entries(config.sourceLayers).map(([id, l]) => ({ id, label: l.label })),
    outputLayers: Object.entries(config.outputLayers).map(([id, l]) => ({ id, label: l.label, isPgm: l.isPgm })),
    pieceTypes: Object.entries(config.pieceTypes).map(([id, t]) => ({
      id,
      label: t.label,
      sourceLayer: t.sourceLayer,
      lifespan: t.lifespan,
      params: Object.entries(t.params).map(([name, p]) => ({ name, type: p.type, required: p.required })),
    })),
    actions: Object.entries(config.actions).map(([id, a]) => ({
      id,
      label: a.label,
      steps: a.steps.map((step) => ('stop' in step ? `stop ${step.stop}` : `${step.mode === 'queue' ? 'queue' : 'fire'} ${step.pieceType}`)),
    })),
    adlibPanels: config.adlibPanels.map((p) => ({
      id: p.id,
      label: p.label,
      items: p.items.map((item) => `${item.label}${item.hotkey === null ? '' : ` (${item.hotkey})`}`),
    })),
    minimumTakeSpanMs: config.settings.minimumTakeSpanMs,
  };
}

/** A config document pretty-printed for the editor. */
export function configText(document: Readonly<Record<string, JsonValue>>): string {
  return JSON.stringify(document, null, 2);
}
